import type React from "react";
import { useEffect, useState } from "react";
import { Camera } from "lucide-react";
import { toast } from "sonner";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import { fetchCompanyMember, updateCompanyMember } from "../../features/auth/authAPI";
import { getMemberRoleLabel } from "../../features/companyMembers/companyMemberAPI";
import { fetchFiltersDepartments } from "../../features/filters/filterSlice";
import { fetchAndRestoreUserData, updateProfilePicture, ensureCurrentUser } from "../../features/auth/authSlice";
import { uploadFile, selectFile } from "../../utils/fileUploadUtils";
import { ProfileImage } from "../ProfileImage";
import FormDropdown from "../form-dropdown";
import PhoneCountrySelect from "../phone-country-select";
import PhoneInput, {
    isValidPhoneNumber,
    parsePhoneNumber,
    type Country,
} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useNavigate } from "react-router-dom";

interface PersonalFormData {
    firstName: string;
    middleName: string;
    lastName: string;
    designation: string;
    department: string;
    email: string;
    contactNumber: string;
    role: string;
    groups: string[];
    gender: string;
}

const PersonalInformation: React.FC = () => {
    const user = useAppSelector((state) => state.auth.user);
    const accessToken = useAppSelector((state) => state.auth.accessToken);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const departments = useAppSelector((state) => state.filters.departments);
    const departmentsStatus = useAppSelector((state) => state.filters.departmentsStatus);
    const departmentsError = useAppSelector((state) => state.filters.departmentsError);

    const [formData, setFormData] = useState<PersonalFormData>({
        firstName: "",
        middleName: "",
        lastName: "",
        designation: "",
        department: "",
        email: "",
        contactNumber: "",
        role: "",
        groups: [],
        gender: "",
    });
    const [profilePictureId, setProfilePictureId] = useState<number | null>(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [phoneCountry, setPhoneCountry] = useState<Country | undefined>(undefined);

    useEffect(() => {
        dispatch(fetchFiltersDepartments());
    }, [dispatch]);

    useEffect(() => {
        if (!user && accessToken) {
            dispatch(fetchAndRestoreUserData());
            return;
        }

        if (!accessToken) return;

        let isMounted = true;
        const loadCompanyMemberData = async () => {
            try {
                // Fetch current user to get company_member_id and role (groups)
                const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
                const companyMemberId = ensuredUser.company_member_id as number | undefined;
                const groups = ensuredUser.groups ?? [];
                const role = groups.length > 0 ? groups[0] : "";
                const userName = ensuredUser.name || user?.name || "";
                const userEmail = ensuredUser.email || user?.email || "";

                // Check if user is admin (no groups or empty groups array)
                const isAdmin = groups.length === 0;

                if (isAdmin) {
                    // For admin: set designation to "admin", firstName to user's name, keep all else blank
                    if (!isMounted) return;
                    setFormData({
                        firstName: userName,
                        middleName: "",
                        lastName: "",
                        designation: "admin",
                        department: "",
                        email: userEmail,
                        contactNumber: "",
                        role: role ?? "",
                        groups: groups,
                        gender: "",
                    });
                    setPhoneCountry(undefined);
                    return;
                }

                if (!companyMemberId) {
                    console.error("Company member ID not found");
                    return;
                }

                const response = await fetchCompanyMember(companyMemberId, accessToken);
                if (!isMounted) return;
                const data = response.data;
                if (!data) return;

                const contactNumber = data.contact_number ?? "";
                let resolvedPhoneCountry: Country | undefined;
                if (contactNumber) {
                    try {
                        resolvedPhoneCountry = parsePhoneNumber(contactNumber)?.country;
                    } catch {
                        resolvedPhoneCountry = undefined;
                    }
                } else {
                    resolvedPhoneCountry = undefined;
                }

                setPhoneCountry(resolvedPhoneCountry);
                setFormData((prev) => {
                    const normalizedGroups: string[] = Array.isArray(data.groups)
                        ? data.groups
                        : (data.group ? [data.group as string] : prev.groups);
                    return {
                        ...prev,
                        firstName: data.first_name ?? prev.firstName,
                        middleName: data.middle_name ?? prev.middleName,
                        lastName: data.last_name ?? prev.lastName,
                        designation: data.designation ?? prev.designation,
                        department: data.department?.name ?? prev.department,
                        email: data.email ?? prev.email,
                        contactNumber,
                        role: normalizedGroups[0] ?? role ?? prev.role,
                        groups: normalizedGroups,
                        gender: data.gender ?? prev.gender,
                    };
                });

                // Set existing profile picture if available
                if (data.profile_picture) {
                    if (typeof data.profile_picture === "number") {
                        setProfilePictureId(data.profile_picture);
                    } else if (
                        typeof data.profile_picture === "object" &&
                        "id" in data.profile_picture
                    ) {
                        setProfilePictureId((data.profile_picture as { id: number }).id);
                    }
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Failed to fetch company member:", error);
            }
        };

        loadCompanyMemberData();

        return () => {
            isMounted = false;
        };
    }, [user, user?.company_member_id, accessToken, user?.groups, dispatch]);

    // Cleanup preview URL on unmount
    useEffect(() => {
        return () => {
            if (profilePicturePreview) {
                URL.revokeObjectURL(profilePicturePreview);
            }
        };
    }, [profilePicturePreview]);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleImageUpload = async () => {
        if (!accessToken) {
            toast.error("Authentication required to upload image");
            return;
        }

        try {
            // Open file picker
            const file = await selectFile("image/*");
            if (!file) {
                return; // User cancelled
            }

            // Validate file type
            if (!file.type.startsWith("image/")) {
                toast.error("Please select a valid image file");
                return;
            }

            // Validate file size (e.g., max 5MB)
            const maxSize = 10 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                toast.error("Image size must be less than 5MB");
                return;
            }

            setIsUploading(true);

            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setProfilePicturePreview(previewUrl);

            // Upload file
            const fileId = await uploadFile(file, accessToken);

            if (fileId) {
                setProfilePictureId(fileId);
                toast.success("Image uploaded successfully");
            } else {
                setProfilePicturePreview(null);
                toast.error("Failed to upload image. Please try again.");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            setProfilePicturePreview(null);
            toast.error("Failed to upload image. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleSave = async () => {
        if (!accessToken) return;

        try {
            setIsSaving(true);

            let companyMemberId = user?.company_member_id;

            // Fetch current user to get company_member_id
            if (!companyMemberId) {
                const ensuredUser = await dispatch(ensureCurrentUser()).unwrap();
                companyMemberId = ensuredUser.company_member_id as number | undefined;
            }

            if (!companyMemberId) {
                toast.error("Company member ID not found");
                setIsSaving(false);
                return;
            }

            const selectedDept = departments.find(
                (dept) => dept.name === formData.department,
            );

            const payload: {
                first_name: string;
                middle_name: string;
                last_name: string;
                designation: string;
                gender: string;
                department: number | null;
                contact_number?: string;
                profile_picture?: number | null;
            } = {
                first_name: formData.firstName,
                middle_name: formData.middleName,
                last_name: formData.lastName,
                designation: formData.designation,
                gender: formData.gender,
                department: selectedDept ? selectedDept.id : null,
                profile_picture: profilePictureId || null,
            };

            // Only include contact_number if it has a value
            if (formData.contactNumber) {
                payload.contact_number = formData.contactNumber;
            }

            await updateCompanyMember(companyMemberId, accessToken, payload);

            // Update profile picture in Redux state so Header can reflect the change
            dispatch(updateProfilePicture(profilePictureId));

            toast.success("Profile updated successfully");
        } catch (error: unknown) {
            const message =
                error instanceof Error
                    ? error.message
                    : "Failed to save changes. Please try again.";
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Profile Photo Section */}
            <div className="flex items-center gap-4 mb-8">
                <div className="relative">
                    <div className="w-24 h-24 bg-gray-400 rounded-full border-2 border-black flex items-center justify-center overflow-hidden">
                        {profilePicturePreview ? (
                            <img
                                src={profilePicturePreview}
                                alt="Profile preview"
                                className="w-24 h-24 rounded-full object-cover"
                            />
                        ) : profilePictureId ? (
                            <ProfileImage
                                profilePicture={profilePictureId}
                                accessToken={accessToken || undefined}
                                alt="Profile"
                                className="w-24 h-24 rounded-full object-cover"
                                fallbackPath="/assets/images/user-Avatar.png"
                            />
                        ) : (
                            <img
                                src="/assets/images/user-Avatar.png"
                                alt="Profile placeholder"
                                className="w-24 h-24 rounded-full object-cover"
                            />
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleImageUpload}
                        disabled={isUploading || !accessToken}
                        className="absolute bottom-0 right-0 bg-[#ABD405] p-2 rounded-full border-2 border-black cursor-pointer hover:bg-lime-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Upload Photo"
                    >
                        {isUploading ? (
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-md animate-spin" />
                        ) : (
                            <Camera size={16} className="text-[#000000]" />
                        )}
                    </button>
                </div>
                <div>
                    <p className="font-medium text-[#000000]">Upload your photo</p>
                    {isUploading && (
                        <p className="text-sm text-gray-500">Uploading...</p>
                    )}
                </div>
            </div>

            {/* Form Fields Grid */}
            <div className="grid grid-cols-3 gap-6">
                {/* First Name */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        First name
                    </label>
                    <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 font-normal rounded-md bg-[#F9F9F9] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.firstName ? "text-gray-800" : "text-gray-400"}`}
                        placeholder="Enter first name"
                        autoComplete="off"
                    />
                </div>

                {/* Middle Name */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Middle name
                    </label>
                    <input
                        type="text"
                        name="middleName"
                        value={formData.middleName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.middleName ? "text-gray-800" : "text-gray-400"}`}
                        placeholder="Enter middle name"
                        autoComplete="off"
                    />
                </div>

                {/* Last Name */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Last name
                    </label>
                    <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.lastName ? "text-gray-800" : "text-gray-400"}`}
                        placeholder="Enter last name"
                        autoComplete="off"
                    />
                </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
                {/* Designation */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Designation
                    </label>
                    <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.designation ? "text-gray-800" : "text-gray-400"}`}
                        placeholder="Enter designation"
                        autoComplete="off"
                        disabled
                    />
                </div>

                {/* Department */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Department
                    </label>
                    <FormDropdown
                        value={formData.department}
                        placeholder="Select Department"
                        disabled
                        options={departments.map((dept) => ({
                            label: dept.name,
                            value: dept.name,
                        }))}
                    />
                    {departmentsStatus === "loading" && (
                        <span className="text-gray-400 text-sm ml-2">Loading...</span>
                    )}
                    {departmentsStatus === "failed" && (
                        <span className="text-red-500 text-sm ml-2">{departmentsError || "Failed to load departments."}</span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
                {/* Email Address */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Email address
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 rounded-md bg-[#F9F9F9] font-normal placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-lime-400 ${formData.email ? "text-gray-800" : "text-gray-400"}`}
                        placeholder="Enter email address"
                        autoComplete="off"
                    />
                </div>

                {/* Contact Number */}
                <div>
                    <label className="block text-base font-medium text-[#000000] mb-2">
                        Contact number
                    </label>
                    <PhoneInput
                        international
                        country={phoneCountry}
                        onCountryChange={setPhoneCountry}
                        countrySelectComponent={PhoneCountrySelect}
                        value={formData.contactNumber}
                        onChange={(val) => setFormData(prev => ({
                            ...prev, contactNumber: val ?? ''
                        }))}
                        className={`phone-input-wrapper ${formData.contactNumber ? "text-gray-800" : "text-gray-400"}`}
                    />
                    {formData.contactNumber &&
                        !isValidPhoneNumber(formData.contactNumber) && (
                            <div className="text-red-500 text-sm">Please enter a valid phone number</div>
                        )
                    }
                </div>
            </div>

            <div>
                <label className="block text-base font-medium text-[#000000] mb-2">
                    Roles
                </label>
                {formData.groups.length === 0 ? (
                    <div className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] text-gray-400">
                        No roles assigned
                    </div>
                ) : (
                    <div className="w-full px-4 py-2 rounded-md bg-[#F9F9F9] text-gray-800">
                        {formData.groups.map((g) => getMemberRoleLabel(g)).join(", ")}
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4">
                <button
                    type="button"
                    onClick={() => navigate("/change-password")}
                    className="btn-cta px-6 py-2 font-semibold rounded-md"
                >
                    Change Password
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="btn-cta px-8 py-2 font-semibold rounded-md disabled:opacity-60"
                >
                    {isSaving ? "Saving..." : "Save"}
                </button>
            </div>
        </div>
    );
};

export default PersonalInformation;
